import deepClone from 'lodash/cloneDeep';
import { createSelector } from 'reselect';
import arrayToObject from 'utils/arrayToObject';
import { POLIMER, SUBSTANCE } from 'utils/constants/coreConstants';
import { DISTRIBUIDOR } from 'utils/constants/reach_role';
import { needsScenario } from 'utils/shipments';

export const lookUpIdValueSelector = (storeTag, fields, filterFunction) => createSelector(
  [getStoreById(storeTag, filterFunction)],
  dataStoreById => {
    const keys = Object.keys(dataStoreById);
    const lookUp = {};
    keys.forEach(key => lookUp[key] = fields.map(field => dataStoreById[key][field]).join(' - '));
    return lookUp;
  }
);

/**
 * deepClone is being used on the under selectors because
 * material-table mutates the state directly, to avoid this
 * problem and conflicts when there are multiple tables on the same view
 * we use deepClone.
 * (otherwise, warnings appear due to react key property collisions
 * inside material table)
 * To introduce immutability a selector that only clones is used
 */

const getStore = storeTag => state => state[storeTag];
const getStoreData = storeTag => state => state[storeTag].data;
const getStoreById = (storeTag, filterFunction) => state => filterFunction ?
  arrayToObject(state[storeTag].data.filter(filterFunction), 'id') :
  state[storeTag].models;

const getStoreDataByPropValue = (storeTag, prop, property) =>
  (state, props) =>
    state[storeTag].data.filter(i => {
        if (typeof property === 'string') {
          return i[property] === props[prop];
        } else {
          // TODO ask for change in API do not use for scenarios id_fluxogram = Object use integer id
          return i[Object.keys(property)[0]] && i[Object.keys(property)[0]][Object.values(property)[0]] === props[prop];
        }
      }
    );

export const getImmutableStore = storeTag => createSelector(
  [getStore(storeTag)],
  data => deepClone(data)
);

export const getImmutableStoreData = storeTag => createSelector(
  [getStoreData(storeTag)],
  data => deepClone(data)
);

export const getImmutableStoreById = storeTag => createSelector(
  [getStoreById(storeTag)],
  data => deepClone(data)
);

/**
 * Generic filters
 */
export const filterByFieldSelector = (storeTag, field, values) => createSelector(
  [getStoreData(storeTag)],
  data => {
    let clonedData = deepClone(data);
    if (typeof field === 'string') {
      if (Array.isArray(values)) {
        return clonedData.filter(item => values.includes(item[field]));
      } else {
        return clonedData.filter(item => item[field] === values);
      }
    } else if (Array.isArray(field)) {
      field.forEach((fd, index) => {
        if (Array.isArray(values[index])) {
          clonedData = clonedData.filter(item => values.includes(item[fd]));
        } else {
          clonedData = clonedData.filter(item => item[fd] === values);
        }
      });
    }
  }
);

export const filterByPropFieldSelector = (storeTag, prop, property) => createSelector(
  [getStoreDataByPropValue(storeTag, prop, property)],
  data => deepClone(data)
);

export const filterByFunctionSelector = (storeTag, filterFunction) => createSelector(
  [getStoreData(storeTag)],
  data => {
    const clonedData = deepClone(data);
    return clonedData.filter(filterFunction);
  }
);

/**
 * A slice indexed by one of its columns, built once and reused.
 *
 * The joins the screens ask for are always the same two shapes - "the
 * composition rows of this product" and "the products this raw material is in"
 * - and both are the same slice read by a different key. Answering either with
 * a filter walks every row on every render: 189 products against 1152
 * composition rows is 217,728 comparisons, and the same question asked from
 * inside a loop over raw materials was two million.
 *
 * An index costs one pass when the slice changes and nothing afterwards, which
 * is what `createSelector` is for. The screens then look up rather than search.
 *
 * ⚠️ No deepClone here, unlike every selector above. That clone exists because
 * material-table mutates what it is handed; an index is not handed to a table,
 * it is read from. Whatever goes into a table is cloned at that point. Cloning
 * here would put the cost straight back.
 */
export const groupBy = (storeTag, key) => createSelector(
  [getStoreData(storeTag)],
  rows => rows.reduce((acc, row) => {
    const k = row[key];
    if (!acc[k]) {
      acc[k] = [];
    }
    acc[k].push(row);
    return acc;
  }, {})
);

/**
 * The joins that replace the nested payloads.
 *
 * Each one below is named after the field it stands in for, because the mapping
 * is the part that is easy to get wrong and impossible to see afterwards: a
 * screen that joins on the wrong column still renders a list of plausible rows.
 *
 * They are selectors, not factories, because none of them takes an argument -
 * their only input is the state - so every screen can share one memo. Building
 * them here rather than in each screen is not tidiness: `createSelector` keeps a
 * single result, so forty modules each calling `groupBy('mixtures', ...)` build
 * and rebuild forty copies of the same index.
 *
 * ⚠️ `|| []` at every read of an index. The fields these replace were always
 * arrays - the serializer sent `[]` - but an index only has keys for parents
 * that have rows, so the empty case reads back `undefined` and `.length` throws.
 * With no ErrorBoundary in the app that blanks the page for a raw material that
 * merely has no shipments. The two selectors that walk their parent slice -
 * `substancesWithRegistersByShipment`, `shipmentsBySubstance` - do answer `[]`,
 * and say so; reading them through `|| []` anyway costs nothing.
 *
 * ⚠️ No deepClone, for the reason given above `groupBy`. Whatever is handed to
 * material-table is cloned at that point, by the screen that hands it over.
 */

/** `rm.raw_material_suppliers` - the shipments of a raw material. */
export const shipmentsByRawMaterial = groupBy('rawMaterialSuppliers', 'raw_material_id');

/**
 * `rm.substances` - what a mixture is made of.
 *
 * The `mixtures` slice holds mixture_substance rows, so both directions of the
 * relation come out of it and the two names read backwards: a mixture's
 * substances are the rows keyed by `mixture_id`, and a substance's mixtures are
 * the rows keyed by `substance_id`. Inverting them is the one mistake in this
 * conversion that produces a full, wrong-looking-at-nothing table.
 */
export const mixtureSubstancesByMixture = groupBy('mixtures', 'mixture_id');

/** `rm.mixtures` - the mixtures a substance is in. See the note above. */
export const mixtureSubstancesBySubstance = groupBy('mixtures', 'substance_id');

/**
 * `shipment.direct_registers` - the registrations that belong to the shipment
 * itself rather than to one substance inside it.
 *
 * The model said `supplier_registers.filter(mixture_substance=None)`; the null
 * is the whole distinction, since the same table holds both kinds. `groupBy`
 * cannot express it, which is why this one is written out.
 */
const isDirectRegister = register =>
  register.mixture_substance_id === null || register.mixture_substance_id === undefined;

export const directRegistersByShipment = createSelector(
  [getStoreData('supplierRegister')],
  registers => registers.reduce((acc, register) => {
    if (!isDirectRegister(register)) {
      return acc;
    }
    const k = register.raw_material_supplier_id;
    if (!acc[k]) {
      acc[k] = [];
    }
    acc[k].push(register);
    return acc;
  }, {})
);

/**
 * Does this shipment need an exposure scenario, by shipment id.
 *
 * `needsScenario` reads `shipment.direct_registers`, and a flat shipment does
 * not have one - so calling it on a row straight out of the slice answers
 * "yes" for every shipment that is not a distributor, quietly, on six screens.
 * This hands it the registrations it is missing instead of restating the rule,
 * so there is still one place where the rule lives.
 */
export const needsScenarioByShipment = createSelector(
  [getStoreData('rawMaterialSuppliers'), directRegistersByShipment],
  (shipments, registersByShipment) => shipments.reduce((acc, shipment) => {
    acc[shipment.id] = needsScenario({
      ...shipment,
      direct_registers: registersByShipment[shipment.id] || []
    });
    return acc;
  }, {})
);

/**
 * `shipment.substances_with_registers` - the substances of the shipment's raw
 * material, each carrying the registrations that are this shipment's.
 *
 * Four levels of nesting in the old payload, and the serializer built it by
 * hand: the mixture_substance rows of the shipment's raw material, and inside
 * each one the supplier_registers narrowed to that shipment. Two keys answer
 * it - `mixture_substance_id` says which substance, `raw_material_supplier_id`
 * says which shipment - so the registers are indexed by both at once and read
 * by the pair.
 *
 * The `supplier_registers` field is kept, rather than left to the caller to
 * join, because all four screens that read this want the pairing and nothing
 * else, and because reproducing the field means the screens change by a lookup
 * instead of by a rewrite. It costs one pass over the registers per slice
 * change, against the old cost of building the same thing server-side on every
 * raw materials fetch.
 *
 * Every shipment gets an entry, `[]` included, the way the serializer did.
 */
export const substancesWithRegistersByShipment = createSelector(
  [
    getStoreData('rawMaterialSuppliers'),
    mixtureSubstancesByMixture,
    getStoreData('supplierRegister')
  ],
  (shipments, substancesOfMixture, registers) => {
    const byShipmentAndSubstance = registers.reduce((acc, register) => {
      const shipmentKey = register.raw_material_supplier_id;
      const substanceKey = register.mixture_substance_id;
      if (!acc[shipmentKey]) {
        acc[shipmentKey] = {};
      }
      if (!acc[shipmentKey][substanceKey]) {
        acc[shipmentKey][substanceKey] = [];
      }
      acc[shipmentKey][substanceKey].push(register);
      return acc;
    }, {});

    return shipments.reduce((acc, shipment) => {
      const ofThisShipment = byShipmentAndSubstance[shipment.id] || {};
      acc[shipment.id] = (substancesOfMixture[shipment.raw_material_id] || []).map(
        mixtureSubstance => ({
          ...mixtureSubstance,
          supplier_registers: ofThisShipment[mixtureSubstance.id] || []
        })
      );
      return acc;
    }, {});
  }
);

/**
 * Every shipment that reaches a substance: its own, and the shipments of every
 * mixture it is part of.
 *
 * Sixteen files walk this, and all sixteen walk it the same way - the direct
 * shipments first, then the mixtures in turn - because they all need to say
 * which raw material the shipment arrived through, to label it or to tag the
 * row as direct or indirect. So an entry is the pair, not the shipment:
 * `{ rawMaterialId, shipment }`, where `rawMaterialId === substance.id` is what
 * "direct" means and anything else is the mixture it came through.
 *
 * Order is the concatenation the screens built by hand, direct before mixtures,
 * so a table that was not sorted keeps the rows where they were.
 *
 * Every raw material gets an entry, `[]` included.
 */
/**
 * `flow.raw_materials` - which raw materials a flowchart uses.
 *
 * It was a list of ids on the flow, and it went with the rest: leaving it in
 * `fields` was what made DRF regenerate it as a writable, required field, and
 * `POST /flows/` began answering "This field is required" for every role.
 *
 * The two join tables say it without the copy: a flow has steps, and a step has
 * step_raw_materials. Deduplicated, because a raw material used on three steps
 * of one flow was one entry in the old list and would be three here.
 *
 * Every flow gets an entry, `[]` included, so a caller can read it without
 * checking - which matters, since one of the two call sites feeds it straight
 * into `useState` and the old value was never undefined either.
 */
export const rawMaterialIdsByFlow = createSelector(
  [getStoreData('flows'), groupBy('steps', 'flow_id'), groupBy('stepRawMaterials', 'step_id')],
  (flows, stepsOf, rawMaterialsOf) => flows.reduce((acc, flow) => {
    const seen = new Set();
    for (const step of stepsOf[flow.id] || []) {
      for (const row of rawMaterialsOf[step.id] || []) {
        seen.add(row.raw_material_id);
      }
    }
    acc[flow.id] = [...seen];
    return acc;
  }, {})
);

export const shipmentsBySubstance = createSelector(
  [getStoreData('rawMaterials'), shipmentsByRawMaterial, mixtureSubstancesBySubstance],
  (rawMaterials, shipmentsOf, mixturesOf) => rawMaterials.reduce((acc, rawMaterial) => {
    const direct = (shipmentsOf[rawMaterial.id] || []).map(
      shipment => ({ rawMaterialId: rawMaterial.id, shipment }));

    const throughMixtures = (mixturesOf[rawMaterial.id] || []).reduce(
      (rows, mixtureSubstance) => rows.concat(
        (shipmentsOf[mixtureSubstance.mixture_id] || []).map(
          shipment => ({ rawMaterialId: mixtureSubstance.mixture_id, shipment }))
      ), []);

    acc[rawMaterial.id] = direct.concat(throughMixtures);
    return acc;
  }, {})
);

/**
 * Specific filters
 */

/**
 --- stepRawMaterials
 Needs to go to Flows to know wich steps make part of that flow
 Nees to know the rawMaterial in question
 Crosses both to find if there are stepRawMaterials for that rawMaterial
 */

const getStepRawMaterials = () =>
  (state, props) => state.stepRawMaterials.data.filter(i =>
    i['raw_material_id'] === props.rawMaterialId
    && props.flowSteps.map(s => s.id).includes(i['step_id'])
  );

export const filterStepRawMaterials = () => createSelector(
  [getStepRawMaterials()],
  data => deepClone(data)
);

/**
 --- substanceWithDirectAndIndirectShipment
 Goes to raw Materials Filters substances with shipment
 or substance that are in Mixs that have Shipments
 Of The shipments mentioned above only the ones chosen are:
 needsScenario(shipment)
 */

// Whether a substance needs a scenario, read entirely from the flat slices.
// Shared by the two filters below, which had the same forty lines copied twice
// with one extra condition between them.
const wantsScenario = (rm, shipmentsOf, mixturesOf, substancesOf, needsScenarioOf) => {
  for (const shipment of shipmentsOf[rm.id] || []) {
    if (needsScenarioOf[shipment.id]) {
      return true;
    }
  }

  for (const mix of mixturesOf[rm.id] || []) {
    for (const shipment of shipmentsOf[mix.mixture_id] || []) {
      if (shipment.reach_role === DISTRIBUIDOR) {
        continue;
      }
      const withRegisters = substancesOf[shipment.id] || [];
      // No registers at all is the same case as an empty exposure_scenario:
      // the substance belongs in the list.
      if (!withRegisters.length) {
        return true;
      }
      // `substance_id` is a number now, not the object it used to be.
      const reg = withRegisters.find(register => register.substance_id === rm.id);
      if (!reg
          || reg.supplier_registers.length === 0
          || (reg.supplier_registers[0].exposure_scenario !== 'nao'
              && reg.supplier_registers[0].exposure_scenario !== 'na')) {
        return true;
      }
    }
  }
  return false;
};

const getSubstanceWithDirectAndIndirectShipment = (state, props) => {
  const shipmentsOf = shipmentsByRawMaterial(state);
  const mixturesOf = mixtureSubstancesBySubstance(state);
  const substancesOf = substancesWithRegistersByShipment(state);
  const needsScenarioOf = needsScenarioByShipment(state);
  return state.rawMaterials.data.filter(rm =>
    [SUBSTANCE, POLIMER].includes(rm.type)
    && wantsScenario(rm, shipmentsOf, mixturesOf, substancesOf, needsScenarioOf));
};

export const filterSubstanceWithDirectAndIndirectShipment = () => createSelector(
  [getSubstanceWithDirectAndIndirectShipment],
  data => deepClone(data)
);


// Built once per slice, not per call: the function below runs inside a filter
// over every raw material, so a search here is a search per row.
const compositionByRawMaterial = groupBy('productRawMaterials', 'raw_material_id');

const getDangerousSubstanceWithDirectAndIndirectShipment = (state, props) => {
  const byRawMaterial = compositionByRawMaterial(state);
  const shipmentsOf = shipmentsByRawMaterial(state);
  const mixturesOf = mixtureSubstancesBySubstance(state);
  const substancesOf = substancesWithRegistersByShipment(state);
  const needsScenarioOf = needsScenarioByShipment(state);
  return state.rawMaterials.data.filter(rm => {
      if ([SUBSTANCE, POLIMER].includes(rm.type)) {

        // "Is this substance in some product where it contributes to the
        // hazard". It used to walk rm.products and look each one up in the
        // composition rows; the composition rows already answer it directly,
        // since every one of them names both sides.
        //
        // It is also the more trustworthy of the two: rm.products is the
        // reverse relation as the serializer renders it, and it arrives with
        // duplicates - one raw material lists 1090049 and 1090075 twice each.
        // The join table has one row per pair.
        const dangerousProducts = (byRawMaterial[rm.id] || []).some(
          mp => mp.contributes_to_hazard);

        // The comment that used to sit here said "should reuse the above
        // selector, code repeated", and it was right: the forty lines below it
        // were the other filter's body copied word for word. They are one
        // function now, so the two can no longer drift apart.
        if (dangerousProducts) {
          return wantsScenario(rm, shipmentsOf, mixturesOf, substancesOf, needsScenarioOf);
        }
      }
      return false;
    }
  );
};

export const filterDangerousSubstanceWithDirectAndIndirectShipment = () => createSelector(
  [getDangerousSubstanceWithDirectAndIndirectShipment],
  data => deepClone(data)
);
